import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import { GET_PDP_FROM_THE_COLLECTION } from 'mocks/queries';
import {
  getProductPrimaryOption,
  getDefaultOptionData,
} from 'commons/productUtils';
import convertOptionParamFormat from 'commons/convertOptionParamFormat';
import getVisitorLocationQueryVariables from 'commons/getVisitorLocationQueryVariables';
import { getConstructedImagesForMaterial } from 'data-hooks/useProductCardMaterialPhotos/utils';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import { getConfigPhotosForSelectedOptions } from 'data-hooks/usePDPPhotos/utils';
import useSuspenseQuery from 'commons/useSuspenseQuery';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import useIsBrowser from 'hooks/useIsBrowser';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import useUserLocation from 'global-state/userLocation/useUserLocation';
import PDPCollectionSliderUI from './PDPCollectionSliderUI';
import { convertOptionsToNewFormat, productHasOption } from './utils';
import usePDPOptions from '../state/context/usePDPOptions';
import usePdpSelectedOptionsVariantSku from '../state/pdpSelectedOptionsVariantSku/usePdpSelectedOptionsVariantSku';

const PDPCollectionSliderInner = ({ setDisplayPressComp }) => {
  const { error, data: basicDetails } = usePDPBasicDetails();
  const selectedOptionsVariantSku = usePdpSelectedOptionsVariantSku();

  const {
    loading,
    data: { selectedOptionsExtended, upholsteryOptionId } = {},
  } = usePDPOptions();

  const isBrowser = useIsBrowser();

  const isSmallDesktopOnly =
    useMediaQuery({
      query: '(min-width: 1024px) and (max-width: 1199px)',
    }) && isBrowser;

  const { id, meta } = basicDetails || {};

  const userLocation = useUserLocation();
  const {
    data: collectionData,
    loading: collectionLoading,
    error: collectionError,
  } = useSuspenseQuery(GET_PDP_FROM_THE_COLLECTION, {
    variables: {
      slug: meta?.collectionSlug?.replace('collections/', ''),
      offset: 0,
      limit: isSmallDesktopOnly ? 3 : 4,
      forProduct: id,
      ...getVisitorLocationQueryVariables(userLocation?.stateShortName),
    },
    ssr: false,
    skip: !id || !meta?.collectionSlug,
  });

  const [products, updateProducts] = useState(null);
  const [productImgs, updateProductImgs] = useState([]);
  const [collection, updateCollection] = useState(null);
  const [collectionLink, setCollectionLink] = useState('');

  useEffect(() => {
    if (!meta?.id) {
      return;
    }
    if (!meta.collectionSlug) {
      setDisplayPressComp(true);
    }
  }, [meta, setDisplayPressComp]);

  useEffect(() => {
    if (
      !collectionData?.collection ||
      !collectionData?.collection?.products ||
      collectionData?.collection?.slug === collection?.slug
    ) {
      return;
    }
    const { collection: collectionValue } = collectionData;
    updateCollection(collectionValue);
    // If no collection found, display Press component instead
    if (!collectionValue?.products?.length) {
      setDisplayPressComp(true);
      return;
    }
    setCollectionLink(collectionValue.name);
    const finalProducts = [];
    collectionValue.products.forEach(prod => {
      const finalOptions = prod.options?.length
        ? convertOptionsToNewFormat(prod.options)
        : [];
      const urlVal = prod.attributes.filter(attr => attr.code === 'url_key');
      const primaryOption = getProductPrimaryOption(finalOptions);
      const defaultOptionData = getDefaultOptionData(finalOptions);
      const defaultOptionImgs = defaultOptionData?.optionData
        ? getConstructedImagesForMaterial({
            product: prod,
            options: finalOptions,
            forMaterial: defaultOptionData.optionName,
            forMaterialType: defaultOptionData.optionData.name,
          })
        : {};
      finalProducts.push({
        ...prod,
        url: urlVal?.[0]?.value || '',
        options: finalOptions,
        primaryOptionName: primaryOption?.name || '',
        primaryOptionId: primaryOption?.id || null,
        defaultImage: {
          ...(defaultOptionImgs || {}),
        },
      });
    });
    updateProducts(finalProducts);
  }, [collection?.slug, collectionData, setDisplayPressComp]);

  useEffect(() => {
    const setProductImgs = () => {
      if (!products?.length) {
        return;
      }
      // Update collection link with selection
      let newCollectionLink = '';
      const updatedImgs = [];
      products.forEach(prod => {
        const imgs = getConfigPhotosForSelectedOptions(
          selectedOptionsVariantSku,
          selectedOptionsExtended,
          upholsteryOptionId,
          prod,
          prod.options
        );
        // Check if selection is in product options
        const hasSelection =
          prod.primaryOptionId &&
          selectedOptionsExtended?.[prod.primaryOptionId]?.value
            ? productHasOption(
                prod.options,
                selectedOptionsExtended[prod.primaryOptionId].value.id,
                prod.primaryOptionId
              )
            : false;
        // If any product has selection update collection link
        if (hasSelection && !newCollectionLink) {
          const optQSValues = convertOptionParamFormat(
            prod.primaryOptionName,
            selectedOptionsExtended[prod.primaryOptionId].value.value,
            'qs'
          );
          newCollectionLink =
            optQSValues.finalOptionName && optQSValues.finalOptionValue
              ? `/?optionName=${optQSValues.finalOptionName}&optionValue=${optQSValues.finalOptionValue}`
              : '';
          if (newCollectionLink) {
            setCollectionLink(collection.slug + newCollectionLink);
          }
        }
        const selectionQS = hasSelection
          ? `?${prod.primaryOptionName.toLowerCase()}=${selectedOptionsExtended[
              prod.primaryOptionId
            ].value.value
              .toLowerCase()
              .split(' ')
              .join('-')}`
          : '';
        if (imgs?.length && hasSelection) {
          updatedImgs.push({ ...imgs[0], url: prod.url + selectionQS });
        } else {
          const finalSrc = prod.defaultImage
            ? prod.primaryOptionId
              ? prod.defaultImage.transparent_config_image
              : prod.defaultImage.transparent_hero_image
            : '';
          updatedImgs.push({
            id: 1,
            src: finalSrc,
            url: prod.url,
          });
        }
      });

      updateProductImgs(updatedImgs);
    };

    if (!products?.length) {
      return;
    }
    setProductImgs();
  }, [
    collection?.slug,
    products,
    selectedOptionsVariantSku,
    selectedOptionsExtended,
    upholsteryOptionId,
  ]);

  if (error || collectionError || !id) {
    return <div className="PDPCollectionSlider__filler" />;
  }

  return (
    <PDPCollectionSliderUI
      productImgs={productImgs}
      loading={collectionLoading || loading}
      collectionSlug={collection?.name}
      collectionLink={collectionLink}
    />
  );
};

PDPCollectionSliderInner.propTypes = {
  setDisplayPressComp: PropTypes.func,
};

const PDPCollectionSlider = props => (
  <SuspenseWithLoader>
    <PDPCollectionSliderInner {...props} />
  </SuspenseWithLoader>
);

export default withBrowserOnly(PDPCollectionSlider);
